import { IDBPDatabase, openDB } from 'idb'
import { IOperation } from '@/contracts/system-modules/sync/operation/IOperation'
import { IClock } from '@/helpers/operationTime'

// Хранилище обмена: IndexedDB, своя база на учётную запись, переживает перезагрузку
// страницы и вмещает операции прикладных модулей (localStorage на 5 МБ для них мал).
//   queue      — операции, ждущие отправки (ключ — метка);
//   operations — операции наборов, открытых пользователю: принятые узлом свои и полученные
//                от узла (ключ — метка, индекс по набору и записи);
//   meta       — курсор и часы.
const PREFIX = 'extasy.sync.'
const VERSION = 1
const QUEUE = 'queue'
const OPERATIONS = 'operations'
const META = 'meta'
const SET_RECORD = 'set_record'
const CURSOR = 'cursor'
const CLOCK = 'clock'

type SyncDb = IDBPDatabase<unknown>

const databases = new Map<string, Promise<SyncDb>>()

function database(userUuid: string): Promise<SyncDb> {
  let db = databases.get(userUuid)
  if (!db) {
    db = openDB(`${PREFIX}${userUuid}`, VERSION, {
      upgrade(upgrading) {
        upgrading.createObjectStore(QUEUE, { keyPath: 'time' })
        upgrading.createObjectStore(OPERATIONS, { keyPath: 'time' }).createIndex(SET_RECORD, ['set', 'record'])
        upgrading.createObjectStore(META)
      },
    })
    databases.set(userUuid, db)
  }

  return db
}

/** Забыть открытые базы: после подмены indexedDB в тестах */
export function closeSyncStorage(): void {
  databases.forEach((db) => db.then((opened) => opened.close()).catch(() => undefined))
  databases.clear()
}

export async function readQueue(userUuid: string): Promise<IOperation[]> {
  return (await database(userUuid)).getAll(QUEUE)
}

export async function putQueued(userUuid: string, operation: IOperation): Promise<void> {
  await (await database(userUuid)).put(QUEUE, operation)
}

export async function removeQueued(userUuid: string, times: string[]): Promise<void> {
  const transaction = (await database(userUuid)).transaction(QUEUE, 'readwrite')
  await Promise.all([...times.map((time) => transaction.store.delete(time)), transaction.done])
}

export async function readCursor(userUuid: string): Promise<number> {
  const cursor = await (await database(userUuid)).get(META, CURSOR)

  return typeof cursor === 'number' ? cursor : 0
}

export async function writeCursor(userUuid: string, cursor: number): Promise<void> {
  await (await database(userUuid)).put(META, cursor, CURSOR)
}

export async function readClock(userUuid: string): Promise<IClock> {
  const clock = await (await database(userUuid)).get(META, CLOCK)

  return clock && typeof clock.millis === 'number' && typeof clock.counter === 'number' ? clock : { millis: 0, counter: 0 }
}

export async function writeClock(userUuid: string, clock: IClock): Promise<void> {
  await (await database(userUuid)).put(META, clock, CLOCK)
}

export async function saveOperations(userUuid: string, operations: IOperation[]): Promise<void> {
  if (operations.length === 0) {
    return
  }
  const transaction = (await database(userUuid)).transaction(OPERATIONS, 'readwrite')
  await Promise.all([...operations.map((operation) => transaction.store.put(operation)), transaction.done])
}

export async function readRecordOperations(userUuid: string, set: string, record: string): Promise<IOperation[]> {
  return (await database(userUuid)).getAllFromIndex(OPERATIONS, SET_RECORD, [set, record])
}

/** Ключи обмена прошлой версии в localStorage больше не нужны; данных в них не было */
export function removeLegacyStorage(): void {
  const keys: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key !== null && key.startsWith(PREFIX)) {
      keys.push(key)
    }
  }
  keys.forEach((key) => localStorage.removeItem(key))
}
